import React, { Fragment, useEffect } from 'react';
import {
  MenuItem,
  Select,
  TextField,
  Typography,
  Switch,
  FormGroup,
  FormControlLabel,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import FilterAccordian from '../filterAccordian';
import Breadcrumb from '../common/breadcrumb';
import { userRequest } from '../../auth/requestMethods';
import { Repeat } from '@mui/icons-material';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import * as Yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import CustomTable from '../tables/MuiDataGrid';

const defaultValues = {
  to_conversion_fraction: '1',
  from_conversion_fraction: '1',
  from_unit_of_measure: '',
  to_unit_of_measure: '',
  is_active: 1,
};

const Manage = ({ history }) => {
  useEffect(() => {
    document.title = 'PFSA | UOM Conversions';
  }, []);
  const [modalState, setModalState] = React.useState({
    update: false,
    open: false,
  });
  const [perPage, setPerPage] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [statusFilter, setStatusFilter] = React.useState('All');
  const [appliedFilters, setAppliedFilters] = React.useState([]);
  const [UOMs, setUOMs] = React.useState([]);
  const [dataIsFetching, setDataIsFetching] = React.useState(true);
  const [tableData, setTableData] = React.useState({
    count: 0,
    uomConversions: [],
  });

  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    trigger,
    getValues,
    formState: { errors, isValid, isSubmitted },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  React.useEffect(() => {
    trigger();
    const subscription = watch((value, { name }) => trigger(name));
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  React.useEffect(() => {
    //fetch UOms data from unit of measure
    userRequest()
      .get('/unit-of-measures')
      .then(({ data }) => {
        setUOMs(data.unitOfMeasures);
      })
      .catch((error) => {
        console.log(error);
      });

    setDataIsFetching(true);

    let requestUrl = `/unit-of-measure-conversions?per_page=${perPage}&page=${page}`;

    if (statusFilter === 'ACTIVE') {
      requestUrl += '&is_active=true';
      setAppliedFilters(['is_active']);
    } else if (statusFilter === 'INACTIVE') {
      requestUrl += '&is_active=false';
      setAppliedFilters(['is_active']);
    } else {
      setAppliedFilters([]); // For "ALL" status
    }

    userRequest()
      .get(requestUrl)
      .then(({ data }) => {
        setTableData((prev) => ({
          ...prev,
          uomConversions: data.unitOfMeasureConversions,
          count: data.total,
        }));

        setDataIsFetching(false);
      })
      .catch(({ response }) => {
        console.error(response);
        setDataIsFetching(false);
      });
  }, [statusFilter, perPage, page]);

  function setStatus(id, data) {
    userRequest()
      .post(`/unit-of-measure-conversions/update/${id}`, data)
      .then(() => {
        let umcs = [];
        if (meetFilterConditions(data)) {
          umcs = tableData.uomConversions.map((uom) => {
            if (id === uom.id) uom.is_active = !uom.is_active;
            return uom;
          });
        } else {
          umcs = tableData.uomConversions.filter((umc) => umc.id !== id);
        }
        setTableData({
          count: umcs.length,
          uomConversions: umcs,
        });
      })
      .catch(() => {});
  }

  function meetFilterConditions(data, isUpdate = true) {
    if (!isUpdate) {
      if (tableData.count === perPage) return false;
    }
    if (statusFilter === 'ALL') {
      return true; // Show all data when "ALL" is selected
    }
    if (appliedFilters.length > 0) {
      return statusFilter === (data.is_active ? 'ACTIVE' : 'INACTIVE');
    }
    return true;
  }

  function deleteEntry(id) {
    userRequest()
      .post(`/unit-of-measure-conversions/delete/${id}`)
      .then(() => {
        setTableData((prev) => {
          let umcs = prev.uomConversions.filter((uom) => id !== uom.id);
          return {
            count: umcs.length,
            uom: prev.uoms,
            uomConversions: umcs,
          };
        });
      })
      .catch(() => {});
  }

  const actionMethods = {
    toggleEdit: (row) => {
      setModalState({ open: true, update: row.id });
      reset(
        Object.keys(defaultValues).reduce((reduced, curr) => {
          reduced[curr] = row[curr];
          return reduced;
        }, {})
      );
    },
    toggleActive: (row) => setStatus(row.id, { is_active: !row.is_active }),
    toggleDelete: (row) => deleteEntry(row.id),
  };

  const columns = [
    {
      field: 'from_conversion_fraction',
      headerName: 'From Qty',
      flex: 1,
      type: 'number',
    },
    {
      field: 'from_unit',
      headerName: 'From UOM',
      flex: 1,
    },
    {
      field: 'to_conversion_fraction',
      headerName: 'To Qty',
      flex: 1,
      type: 'number',
    },
    {
      field: 'to_unit',
      headerName: 'To UOM',
      flex: 1,
    },
    {
      field: 'active',
      headerName: 'Status',
      headerAlign: 'center',
      width: 110,
      renderCell: ({ row }) =>
        row.is_active ? (
          <Typography className="font-success mx-auto">Active</Typography>
        ) : (
          <Typography className="font-danger mx-auto">Inactive</Typography>
        ),
    },
  ];

  function getFilters() {
    if (statusFilter === 'ACTIVE') {
      return ['Status: Active'];
    } else if (statusFilter === 'INACTIVE') {
      return ['Status: Inactive'];
    } else {
      return []; // Return an empty array for "ALL" status
    }
  }

  const onSubmit = (data) => {
    if (data !== '') {
      toast.info(`${modalState.update ? 'Updating...' : 'Creating...'}`, {
        isLoading: true,
        position: 'bottom-center',
        autoClose: 5000,
        theme: 'colored',
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastId: 'create',
      });
      let api = modalState.update
        ? `/unit-of-measure-conversions/update/${modalState.update}`
        : '/unit-of-measure-conversions';

      let { from_unit, to_unit, ...rest } = data;

      userRequest()
        .post(api, { ...rest })
        .then((res) => {
          console.log(res);
          toast.dismiss('create');
          toast.success(`Successfully ${modalState.update ? 'updated' : 'created'}`, {
            position: 'bottom-center',
            autoClose: 5000,
            closeOnClick: true,
            draggable: true,
          });
          setTableData((prev) => {
            let uomConversions = [...prev.uomConversions];
            let count = prev.count;
            if (modalState.update) {
              uomConversions = uomConversions.map((worker) => {
                if (worker.id === modalState.update) return res.data.unitOfMeasureConversion;
                return worker;
              });
            } else {
              count += 1;
              uomConversions.push(res.data.unitOfMeasureConversion);
            }
            return { count, uomConversions };
          });
          setModalState({ update: true, open: false });
          reset(defaultValues, {});
        })
        .catch(({ response }) => {
          toast.dismiss('create');
          toast.error(response.data.message, {
            position: 'bottom-center',
            autoClose: 5000,
            closeOnClick: true,
            draggable: true,
          });
        });
    }
  };

  return (
    <Fragment>
      <Breadcrumb icon={<Repeat />} title="Manage" parent="UOM Conversion" />

      <div className="container-fluid">
        <FilterAccordian filters={getFilters()}>
          <div className="form-group row mx-0">
            <FormControl>
              <InputLabel>Status</InputLabel>
              <Select
                name="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
                style={{
                  fontStyle: statusFilter === 'All' ? 'italic' : 'normal',
                }}
              >
                <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                  All
                </MenuItem>
                <MenuItem value={'ACTIVE'}>Active</MenuItem>
                <MenuItem value={'INACTIVE'}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </div>
        </FilterAccordian>
      </div>
      <button
        onClick={() => {
          setModalState({ update: false, open: true });
        }}
        className="btn btn-secondary m-15"
        type="submit"
      >
        <i className="fa fa-plus fa-xl" aria-hidden="true"></i>
      </button>
      <Modal
        isOpen={modalState.open}
        toggle={() => {
          setModalState({ update: false, open: false });
          reset(defaultValues, {});
        }}
        size="lg"
        centered={true}
      >
        <ModalHeader
          toggle={() => {
            setModalState({ update: false, open: false });
          }}
        >
          {modalState.update ? 'Edit' : 'Create'} Unit Of Measure Conversion
        </ModalHeader>
        <ModalBody>
          <div className="card-body">
            <form className="theme-form" onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group row col-3">
                <TextField
                  sx={{
                    '& .css-9ddj71-MuiInputBase-root-MuiOutlinedInput-root': {
                      maxWidth: 'none',
                      width: '413%',
                    },
                  }}
                  id="outlined-number"
                  label="From Qty"
                  type="number"
                  {...register(`from_conversion_fraction`)}
                  error={!!errors.from_conversion_fraction?.message}
                  helperText={errors.from_conversion_fraction?.message}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    style: {
                      color: 'black',
                    },
                  }}
                />
              </div>
              <div className="form-group row">
                <Controller
                  control={control}
                  name="from_unit_of_measure"
                  defaultValue={0}
                  render={({ field }) => (
                    //only show the red highlight if the error is touched.
                    <FormControl
                      error={
                        !!(
                          errors.from_unit_of_measure?.message &&
                          isSubmitted &&
                          getValues().from_unit_of_measure === ''
                        )
                      }
                    >
                      <InputLabel id="demo-simple-select-helper-label">From UOM</InputLabel>
                      <Select
                        name={field.name}
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                        }}
                        labelId="demo-simple-select-helper-label"
                        id="demo-simple-select-helper"
                        label="From UOM"
                      >
                        <MenuItem value={''}>- Select -</MenuItem>
                        {UOMs.map((uom) => (
                          <MenuItem value={uom.id} key={uom.id}>
                            {`${uom.display_name} (${uom.display_unit})`}
                          </MenuItem>
                        ))}
                      </Select>
                      {/* <FormHelperText>{errors.from_unit_of_measure?.message && isSubmitted ? errors.from_unit_of_measure.message : ''}</FormHelperText> */}
                    </FormControl>
                  )}
                />
              </div>
              <br />
              <br />
              <div className="form-group row col-3">
                <TextField
                  sx={{
                    '& .css-9ddj71-MuiInputBase-root-MuiOutlinedInput-root': {
                      maxWidth: 'none',
                      width: '413%',
                    },
                  }}
                  id="outlined-number"
                  label="To Qty"
                  type="number"
                  {...register(`to_conversion_fraction`)}
                  error={!!errors.to_conversion_fraction?.message}
                  helperText={errors.to_conversion_fraction?.message}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    style: {
                      color: 'black',
                    },
                  }}
                />
              </div>
              <div className="form-group row">
                <Controller
                  control={control}
                  name="to_unit_of_measure"
                  defaultValue={0}
                  render={({ field }) => (
                    <FormControl
                      error={
                        !!(errors.to_unit_of_measure?.message && isSubmitted && getValues().to_unit_of_measure === '')
                      }
                    >
                      <InputLabel id="demo-simple-select-helper-label">To UOM</InputLabel>
                      <Select
                        name={field.name}
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                        }}
                        labelId="demo-simple-select-helper-label"
                        id="demo-simple-select-helper"
                        label="To UOM"
                      >
                        <MenuItem value={''}>- Select -</MenuItem>
                        {UOMs.map((uom) => (
                          <MenuItem value={uom.id} key={uom.id}>
                            {`${uom.display_name} (${uom.display_unit})`}
                          </MenuItem>
                        ))}
                      </Select>
                      {/* <FormHelperText>{errors.to_unit_of_measure?.message && isSubmitted ? errors.to_unit_of_measure.message : ''}</FormHelperText>  */}
                    </FormControl>
                  )}
                />
              </div>

              <p className="txt-primary">E.g 1kg = 1000g</p>
              {modalState.update && (
                <div className="d-flex justify-content-start my-4">
                  <FormGroup>
                    <FormControlLabel
                      control={<Switch {...register(`is_active`)} checked={watch('is_active')} />}
                      label="Active Status"
                      sx={{ margin: 0 }}
                      labelPlacement="start"
                    />
                  </FormGroup>
                </div>
              )}
              <div>
                <button className="btn btn-primary me-1" type="submit">
                  {modalState.update ? 'Save' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </ModalBody>
      </Modal>
      <div className="container-fluid">
        <CustomTable
          rows={tableData.uomConversions}
          actions={actionMethods}
          columns={columns}
          setPageSize={setPerPage}
          setPage={setPage}
          rowCount={tableData.count}
          pageSize={perPage}
          loading={dataIsFetching}
        />
      </div>
    </Fragment>
  );
};

const validationSchema = Yup.object().shape({
  to_conversion_fraction: Yup.string()
    .transform((value) => (parseInt(value) <= 0 ? NaN : value))
    .typeError('Incorrect input')
    .required('Required'),
  from_conversion_fraction: Yup.string()
    .transform((value) => (parseInt(value) <= 0 ? NaN : value))
    .typeError('Incorrect input')
    .required('Required'),
  from_unit_of_measure: Yup.string().required('Required'),
  to_unit_of_measure: Yup.string()
    .required('Required')
    .test('not-equal', "To and From UoM types can't be the same", function (value) {
      return value !== this.parent.from_unit_of_measure;
    }),
  is_active: Yup.boolean().required(),
});

export default Manage;
